const $$splitComponentImporter = () => import('/home/runner/work/inspecttec-frontend-admin/inspecttec-frontend-admin/src/routes/clients/create.tsx?tsr-split=component');
import { lazyRouteComponent } from '@tanstack/react-router';
import { Permissions } from "@/constants/permissions";
import { requirePermission } from "@/lib/auth/permission-guard";
import type { CreateClientRequest } from "@/types/api/client";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const createClientSchema = z.object({
  clientName: z.string().min(1, "Client Name is required"),
  domainName: z.string().optional(),
  email: z.string().email("Invalid email address").optional().or(z.literal("")),
  phone: z.string().optional(),
  website: z.string().optional(),
  address1: z.string().optional(),
  address2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  timeZoneId: z.string(),
  // ── Onboarding Phase 1A ──
  ownerFirstName: z.string().optional(),
  ownerLastName: z.string().optional(),
  ownerEmail: z.string().optional(),
  createWithoutOwner: z.boolean(),
  industryKey: z.string().min(1, "Industry is required"),
  associationId: z.string().optional(),
  authMode: z.enum(["ciam", "sso"]),
  tenantId: z.string().optional(),
  applyStarterPack: z.boolean(),
  createBillingTrial: z.boolean()
}).superRefine((values, ctx) => {
  const ownerEmail = values.ownerEmail?.trim() ?? "";
  if (!values.createWithoutOwner) {
    if (!values.ownerFirstName?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ownerFirstName"],
        message: "Owner first name is required"
      });
    }
    if (!values.ownerLastName?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ownerLastName"],
        message: "Owner last name is required"
      });
    }
    if (!ownerEmail) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ownerEmail"],
        message: "Owner e-mail is required"
      });
    }
  }
  if (ownerEmail && !EMAIL_RE.test(ownerEmail)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["ownerEmail"],
      message: "Invalid email address"
    });
  }
  if (values.authMode === "sso") {
    const tenantId = values.tenantId?.trim() ?? "";
    if (!tenantId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["tenantId"],
        message: "Entra tenant ID is required for customer SSO"
      });
    } else if (!GUID_RE.test(tenantId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["tenantId"],
        message: "Tenant ID must be a GUID"
      });
    }
    if (!values.domainName?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["domainName"],
        message: "Domain name is required for SSO tenants (users are matched by e-mail domain)"
      });
    }
  }
});
type CreateClientFormValues = z.infer<typeof createClientSchema>;
type CreateClientFieldName = keyof CreateClientFormValues;

/**
 * Maps a server validation message (400 provisioning precondition / 422
 * FluentValidation, `ApiError.details`) onto the form field it concerns.
 * First match wins; the generic "name" test comes last.
 */

export const Route = createFileRoute("/clients/create")({
  beforeLoad: args => requirePermission(Permissions.Clients.Create, args),
  component: lazyRouteComponent($$splitComponentImporter, 'component')
});